// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import {
  createContext,
  ReactNode,
  useContext,
  useState,
  Children,
  isValidElement,
  cloneElement,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
} from 'react'
import { useBreakpoint, AppBarProps } from '@teamnine/pcj-ui-v2'

import SideNav from '@/app/shared/components/Layout/Navigation/SideNav'

export type LayoutPageHeader = {
  title?: AppBarProps['title']
  mobileTitle?: AppBarProps['title']
  customTitle?: AppBarProps['customSubTitle']
  tabs?: AppBarProps['tabs']
}

export interface LayoutProviderValue {
  isFullScreen: boolean
  setIsFullScreen: Dispatch<SetStateAction<boolean>>
  isNavCollapsed: boolean
  setIsNavCollapsed: (val: boolean) => void
  useAside: boolean
  pageHeader?: LayoutPageHeader
  setPageHeader: (header?: LayoutPageHeader) => void
}

const LayoutContext = createContext<LayoutProviderValue | null>(null)
LayoutContext.displayName = 'RouterContext'

const LayoutProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[]
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [pageHeader, setPageHeader] = useState<LayoutPageHeader | undefined>(
    undefined
  )
  const breakpoint = useBreakpoint()
  const [isNavCollapsed, setIsNavCollapsed] = useState(
    () =>
      breakpoint === 'monitor' ||
      breakpoint === 'laptop' ||
      breakpoint === 'tablet'
  )
  const useAside = useMemo(() => {
    return breakpoint === 'mobile' || breakpoint === 'phone'
  }, [breakpoint])

  useEffect(() => {
    if (
      breakpoint === 'monitor' ||
      breakpoint === 'laptop' ||
      breakpoint === 'tablet'
    ) {
      setIsNavCollapsed(true)
    } else if (breakpoint === 'mobile' || breakpoint === 'phone') {
      setIsNavCollapsed(false)
    }
  }, [breakpoint])

  return (
    <LayoutContext.Provider
      value={{
        isFullScreen,
        setIsFullScreen,
        isNavCollapsed,
        setIsNavCollapsed,
        useAside,
        pageHeader,
        setPageHeader,
      }}
    >
      <SideNav isCollapsed={isNavCollapsed} handleCollapse={setIsNavCollapsed}>
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            return cloneElement(child, { ...child.props })
          }
        })}
      </SideNav>
    </LayoutContext.Provider>
  )
}

const useLayout = (options?: {
  ignoreError?: boolean
}): LayoutProviderValue => {
  const { ignoreError = false } = options || {}
  const context = useContext(LayoutContext)
  if (!context) {
    if (!ignoreError) {
      throw new Error('useLayout must be within a LayoutProvider')
    } else {
      return {} as LayoutProviderValue
    }
  }
  return context
}

export { LayoutProvider, useLayout }
